import { useEffect, useRef } from 'react';
import { channelStatusActionLabels } from 'shared/components/ConversationStatusSelect';
import usePrevious from 'shared/hooks/usePrevious';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { ChannelCreationSourceType, ChannelStatusAction, ChannelViewChannelFragment } from 'shared/types/graphql';

const useTrackChannelView = ({
  channelId,
  createdAt,
  channelStatusAction,
  creationSource,
  unreadMessageCount,
}: {
  channelId: string | undefined;
  createdAt: ChannelViewChannelFragment['createdAt'] | undefined;
  channelStatusAction: ChannelStatusAction | undefined;
  creationSource: ChannelCreationSourceType | undefined;
  unreadMessageCount: number;
}): void => {
  const shouldTrackChannelView = useRef(false);
  const previousChannelId = usePrevious(channelId);
  const { trackEvent } = useTracking();

  /**
   * Queue up tracking the channel view only when the channel ID changes
   */
  useEffect(() => {
    if (channelId && channelId !== previousChannelId) {
      shouldTrackChannelView.current = true;
    }
  }, [channelId, previousChannelId, shouldTrackChannelView]);

  useEffect(() => {
    if (
      shouldTrackChannelView.current &&
      createdAt /* Presence of `createdAt` indicates the request has returned with data */
    ) {
      trackEvent(ConversationEvents.PageViewed, {
        createdAt,
        timeSinceCreation: Date.now() - new Date(createdAt).getTime(),
        unreadMessageCount,
        creationSource: creationSource || 'Unknown',
        statusType: channelStatusAction ? channelStatusActionLabels[channelStatusAction] : 'Unknown',
      });
      shouldTrackChannelView.current = false;
    }
  }, [channelId, unreadMessageCount, previousChannelId, createdAt, creationSource, channelStatusAction, trackEvent]);
};

export default useTrackChannelView;
